import React, { FC, useCallback } from "react";
import { ActionBarConfig, ActionBarFormButton, ActionBarScheduleButton } from "@xapp/stentor-chat-widget";
import ActionBar from "./ActionBar";

export interface ActionBarPreviewProps {
    /**
     * ActionBar configuration including enabled state, buttons, position, and mobile breakpoint
     */
    readonly config: ActionBarConfig;
    /**
     * Whether to show the chat button in active state (for preview purposes)
     * @default false
     */
    readonly chatActive?: boolean;
}

/**
 * ActionBarPreview is a simplified version of ActionBar designed for use in Studio
 * configuration UI. It renders a visual preview of the action bar without requiring
 * interactive callbacks.
 *
 * This component provides no-op handlers for all button interactions, making it
 * suitable for configuration previews where you want to show how the action bar
 * will look without actual functionality.
 *
 * @example
 * ```tsx
 * <ActionBarPreview
 *   config={{
 *     enabled: true,
 *     buttons: [
 *       { id: '1', type: 'chat', label: 'Chat', icon: 'chat' },
 *       { id: '2', type: 'phone', label: 'Call', icon: 'phone', phoneNumber: '555-1234' }
 *     ],
 *     position: 'right'
 *   }}
 * />
 * ```
 */
const ActionBarPreview: FC<ActionBarPreviewProps> = ({ config, chatActive = false }) => {
    // No-op handlers for preview mode
    const handleChatClick = useCallback(() => {
        // Preview only - no action
    }, []);

    const handleChatMinimize = useCallback(() => {
        // Preview only - no action
    }, []);

    const handleFormClick = useCallback((_button: ActionBarFormButton | ActionBarScheduleButton) => {
        // Preview only - no action
    }, []);

    return (
        <ActionBar
            config={config}
            visible={chatActive}
            chatDisabled={false}
            onChatClick={handleChatClick}
            onChatMinimize={handleChatMinimize}
            onFormClick={handleFormClick}
        />
    );
};

export default ActionBarPreview;
